"use client";

import { motion, useInView } from "framer-motion";
import { useRef, useEffect, useState } from "react";
import { Trophy, CalendarDays, Users, Layers } from "lucide-react";

// Smooth ease-out animated counter component
function CounterNumber({ target, duration = 2.2, start = false }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!start) return;

    let startTime = null;
    let animationFrame = null;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / (duration * 1000), 1);

      // Smooth ease-out cubic
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(easedProgress * target));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      } else {
        setCount(target);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, [target, duration, start]);

  return <span className="tabular-nums">{count}</span>;
}

const statsData = [
  { number: 11, label: "Awards", icon: Trophy, badge: "Excellence" },
  { number: 6, label: "Years", icon: CalendarDays, badge: "Since 2020" },
  { number: 220, label: "Members", icon: Users, badge: "Stronger Together" },
  { number: 7, label: "Subunits", icon: Layers, badge: "Dynamic Chapters" },
];

export default function AboutTeamDivider() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-10%" });

  return (
    <section
      ref={ref}
      className="relative py-16 md:py-20 overflow-hidden flex items-center justify-center"
    >
      {/* BACKGROUND IMAGE */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: "url('/bg.webp')",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundAttachment: "fixed",
        }}
      />

      {/* BLUE GRADIENT OVERLAY */}
      <div
        className="absolute inset-0 z-0"
        style={{
          background:
            "linear-gradient(135deg, rgba(0, 102, 153, 0.92) 0%, rgba(0, 34, 51, 0.96) 100%)",
        }}
      />

      {/* MAIN CONTAINER */}
      <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-8 lg:px-12 flex flex-col items-center gap-8">

        {/* TITLE */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center space-y-2"
        >
          <h2 className="text-white text-3xl sm:text-4xl md:text-5xl font-extralight tracking-tight flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-3">
            <span className="font-bold tracking-wide">IEEE ISIMS SB</span>
            <span className="hidden sm:inline text-white/40 font-light">—</span>
            <span className="font-serif italic font-medium text-[#7dd3fc]">
              In Numbers
            </span>
          </h2>
        </motion.div>

        {/* 4 STATS: no boxes, only soft vertical lines. Big white icon with the number on top of it */}
        <div className="grid grid-cols-2 lg:grid-cols-4 w-full gap-y-10 lg:gap-y-0">
          {statsData.map((stat, index) => {
            const Icon = stat.icon;

            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{
                  duration: 0.7,
                  delay: 0.15 + index * 0.12,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="group relative flex flex-col items-center text-center px-2 sm:px-4"
              >
                {/* Vertical divider: grows from the middle, fades at both ends */}
                <motion.span
                  aria-hidden="true"
                  initial={{ scaleY: 0 }}
                  animate={isInView ? { scaleY: 1 } : {}}
                  transition={{ duration: 0.9, delay: 0.4 + index * 0.12, ease: [0.22, 1, 0.36, 1] }}
                  className={`${index % 2 === 1 ? "block" : "hidden"} ${index > 0 ? "lg:block" : ""} absolute left-0 top-[6%] bottom-[6%] w-px bg-gradient-to-b from-transparent via-white/35 to-transparent origin-center`}
                />

                {/* Big white icon; the number sits on its bottom-right corner */}
                <div className="relative w-28 h-28 sm:w-36 sm:h-36 lg:w-40 lg:h-40">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8, rotate: -10 }}
                    animate={isInView ? { opacity: 1, scale: 1, rotate: 0 } : {}}
                    transition={{ duration: 1, delay: 0.3 + index * 0.12, ease: [0.22, 1, 0.36, 1] }}
                    className="absolute inset-0 pointer-events-none"
                  >
                    <Icon
                      aria-hidden="true"
                      strokeWidth={1}
                      className="w-full h-full text-white opacity-[0.3] group-hover:opacity-50 group-hover:scale-105 group-hover:-rotate-6 transition-all duration-500 ease-out"
                    />
                  </motion.div>

                  <div className="absolute right-0 bottom-0 translate-x-[18%] translate-y-[22%] z-10 text-5xl sm:text-6xl lg:text-7xl font-black text-white tracking-tighter flex items-baseline leading-none drop-shadow-[0_2px_14px_rgba(0,34,51,0.65)]">
                    <CounterNumber target={stat.number} duration={2.2} start={isInView} />
                    <span className="text-[#7dd3fc] font-light text-3xl sm:text-4xl ml-0.5">+</span>
                  </div>
                </div>

                <p className="mt-6 sm:mt-7 text-base sm:text-xl font-bold text-white">{stat.label}</p>
                <p className="mt-1 text-xs sm:text-sm text-white/60 font-medium group-hover:text-[#7dd3fc] transition-colors">
                  {stat.badge}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* FOOTER NOTE */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 1, duration: 0.6 }}
          className="text-white/40 text-[11px] tracking-widest uppercase text-center font-medium mt-2"
        >
          THE IEEE Higher Institute of Computer Science & Multimedia of Sfax SB
        </motion.p>
      </div>
    </section>
  );
}